import axios from 'axios';
import { UtilsService } from 'homeday-blocks';
import { stringify as queryStringify } from 'qs';
import CONFIG from '@/config/env';
import { FILTERS } from '@/config/DEAL';
import { FILTERS as STATUS_GROUP_FILTERS } from '@/config/STATUS_GROUP';

function getParamsWithSerializer(params) {
  return {
    params,
    paramsSerializer: queryStringify,
  };
}

export async function createSession({ email, password } = {}) {
  const response = await axios.post(CONFIG.api.sessions, {
    email,
    password,
  });

  return response.data;
}

export async function getUserData({ token } = {}) {
  const _axios = axios.create({
    headers: {
      Authorization: token,
    },
  });

  const response = await _axios.get(CONFIG.api.user_data);

  return response.data;
}

export async function listDeals(filter = FILTERS.FUTURE) {
  const url = UtilsService.populateTemplate(CONFIG.api.deals, { dealUid: '' });
  const response = await axios.get(url, {
    params: {
      filter,
    },
    errorMessage: 'Failed to fetch deals',
  });

  return response.data.data;
}

export async function listAllDeals() {
  const url = UtilsService.populateTemplate(CONFIG.api.deals, { dealUid: '' });
  const response = await axios.get(url, {
    errorMessage: 'Failed to fetch all deals',
  });

  return response.data.data;
}

export async function getDeal(dealUid) {
  const url = UtilsService.populateTemplate(CONFIG.api.deals, { dealUid });
  const response = await axios.get(url, {
    errorMessage: `Failed to get deal: ${dealUid}`,
  });

  return response.data.data;
}

export function signBrokerOrder(params) {
  return axios.post(CONFIG.api.sign_broker_order, params, {
    errorMessage: 'Failed to sign broker order',
  });
}

export async function getPotentialBuyers({ dealUid, prices }) {
  const response = await axios.get(
    CONFIG.api.potential_buyers,
    getParamsWithSerializer({
      deal_uid: dealUid,
      prices: prices.join(','),
    }),
  );

  return response.data;
}

export function createEvaluation(evaluation) {
  return axios.post(
    CONFIG.api.evaluations,
    { evaluation },
    {
      errorMessage: 'Failed to create evaluation',
    },
  );
}

export async function getSimilarProperties(deal) {
  const LIVING_SPACE_DEVIATION = 30;
  const isApartment = deal.propertyType === 'apartment';
  const params = {
    lat: deal.location.lat,
    lng: deal.location.lng,
    property_type: deal.propertyType,
    living_space_min: 0,
    living_space_max: 9999,
  };

  if (isApartment) {
    const livingSpaceMin = deal.livingSpace - LIVING_SPACE_DEVIATION;
    const livingSpaceMax = deal.livingSpace + LIVING_SPACE_DEVIATION;

    params.living_space_min = Math.max(0, livingSpaceMin);
    params.living_space_max = livingSpaceMax;
  }

  const response = await axios.get(CONFIG.api.similar_properties, getParamsWithSerializer(params), {
    errorMessage: 'Failed to fetch similar properties',
  });
  return response.data.data;
}

export async function uploadDocumentFiles({ dealUid, fileName, base64Data }) {
  const url = `${CONFIG.api.document_files}?deal_uid=${dealUid}`;
  const base64Response = await fetch(`data:application/pdf;base64,${base64Data}`);
  const blob = await base64Response.blob();
  const formData = new FormData();
  formData.append('document_file', blob, fileName);
  return axios.post(url, formData, {
    errorMessage: 'Failed to upload document file',
  });
}

export async function getDashboard(filter = STATUS_GROUP_FILTERS.ACTIVE) {
  const url = UtilsService.populateTemplate(CONFIG.api.dashboard, { dealUid: '' });
  const response = await axios.get(url, {
    params: {
      filter,
    },
    errorMessage: `Failed to get dashboard`,
  });

  return response.data;
}

export async function getDashboardDeal({ dealUid }) {
  const url = UtilsService.populateTemplate(CONFIG.api.dashboard, { dealUid });
  const response = await axios.get(url, {
    errorMessage: `Failed to get dashboard deal`,
  });

  return response.data;
}

export async function createDealNote({ dealUid, note }) {
  const url = UtilsService.populateTemplate(CONFIG.api.deal_notes, { dealUid });
  const response = await axios.post(
    url,
    { note },
    {
      errorMessage: `Failed to create deal note`,
    },
  );

  return response.data;
}

export async function acknowledgeHomedayMaximumPricePopup({ dealUid, popupType }) {
  const url = UtilsService.populateTemplate(
    CONFIG.api.homeday_maximum_price_popup_acknowledgments,
    {
      dealUid,
    },
  );
  const response = await axios.post(
    url,
    { popup_type: popupType },
    {
      errorMessage: 'Failed to acknowledge Homeday Maximalpreis popup',
    },
  );

  return response.data;
}

export async function postponeTask({ dealUid, taskUid, postponeTo }) {
  const url = `${UtilsService.populateTemplate(CONFIG.api.deal_tasks, { dealUid })}/postpone`;

  const response = await axios.post(url, UtilsService.keysToSnakeCase({ taskUid, postponeTo }), {
    errorMessage: `Failed to postpone task ${taskUid}`,
  });
  return response.data;
}

export async function completeSimpleTask({ dealUid, taskUid }) {
  const url = `${UtilsService.populateTemplate(CONFIG.api.deal_tasks, { dealUid })}/complete`;

  const response = await axios.post(url, UtilsService.keysToSnakeCase({ taskUid }), {
    errorMessage: `Failed to complete task ${taskUid}`,
  });

  return response.data;
}
